"use client";

import { useState } from "react";
import {
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { RESULT_CARD, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  DoublingMode,
  FinalMode,
  TimeMode,
} from "@/components/tools/biology-lab/doubling-panels";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import type { TimeUnit } from "@/lib/tool-kit/biology-lab/growth";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const CELL_DOUBLING_TIME_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to work out?",
  modeDoubling: "Doubling time from two counts",
  modeFinal: "Cell number after some time",
  modeTime: "Time to reach a target number",
  initial: "Starting number (or cells/mL)",
  final: "Final number (same unit as the start)",
  elapsed: "Time elapsed",
  doubling: "Doubling time",
  target: "Target number (same unit as the start)",
  timeUnit: "Time unit",
  unitMin: "minutes",
  unitHour: "hours",
  unitDay: "days",
  shortMin: "min",
  shortHour: "h",
  shortDay: "d",
  results: "Results",
  doublingTime: "Doubling time",
  rate: "Growth rate μ (per {unit})",
  generations: "Generations",
  fold: "Fold increase",
  percent: "Increase per {unit}",
  alsoSame: "The same doubling time: {parts}.",
  finalCount: "Number after {time} {unit}",
  timeNeeded: "Time to reach the target",
  workingHeading: "The working",
  workingDoubling:
    "Td = t × ln 2 ÷ ln(N ÷ N₀) = {t} × 0.6931 ÷ ln({final} ÷ {initial}) = {result} {unit}",
  workingRate: "μ = ln(N ÷ N₀) ÷ t = {logRatio} ÷ {t} = {result} per {unit}",
  workingGenerations: "generations = log₂(N ÷ N₀) = {result}",
  workingFinal: "N = N₀ × 2^(t ÷ Td) = {initial} × 2^({t} ÷ {td}) = {result}",
  workingTime:
    "t = Td × log₂(target ÷ N₀) = {td} × log₂({target} ÷ {initial}) = {result} {unit}",
  chartHeading: "Growth on a log scale",
  chartLabel:
    "Line chart on a logarithmic count axis showing growth from {initial} to {final} over {time} {unit}, with a marker at each doubling",
  chartX: "Time ({unit})",
  chartY: "Number",
  copyResult: "Copy result",
  copied: "Copied!",
  copyDoubling:
    "Doubling time {td} {unit} ({generations} generations in {t} {unit})",
  none: "The number did not change, so there is no growth and the doubling time is infinite.",
  decline:
    "The number fell, so there is no doubling time. The population halves every {half} {unit} ({generations} generations).",
  phaseNote:
    "These formulas assume exponential (log-phase) growth. If either count was taken in the lag or plateau phase, the result does not describe the cells' real doubling time.",
  errInitial: "Enter a starting number greater than 0.",
  errFinal: "Enter a final number greater than 0.",
  errTime: "Enter a time of 0 or more (greater than 0 for a doubling time).",
  errDoubling: "Enter a doubling time greater than 0.",
  errTarget: "Enter a target number greater than 0.",
  enter: "Enter the numbers and the time.",
} as const;

export type CellDoublingTimeCalculatorStrings = {
  [K in keyof typeof CELL_DOUBLING_TIME_CALCULATOR_STRINGS]: string;
};
type Mode = "doubling" | "final" | "time";

const ID = "dt";

export default function CellDoublingTimeCalculatorWidget() {
  const strings = useToolUi(CELL_DOUBLING_TIME_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("doubling");
  const [initial, setInitial] = useState("2.5e5");
  const [final, setFinal] = useState("1.2e6");
  const [elapsed, setElapsed] = useState("72");
  const [doubling, setDoubling] = useState("24");
  const [target, setTarget] = useState("1e6");
  const [unit, setUnit] = useState<TimeUnit>("h");

  const n0 = parseMeasuredNumber(initial);
  const n1 = parseMeasuredNumber(final);
  const time = parseMeasuredNumber(elapsed);
  const td = parseMeasuredNumber(doubling);
  const goal = parseMeasuredNumber(target);

  function view() {
    if (n0 === null) {
      return <Notice>{strings.enter}</Notice>;
    }
    if (mode === "doubling") {
      return n1 === null || time === null ? (
        <Notice>{strings.enter}</Notice>
      ) : (
        <DoublingMode
          final={n1}
          fmt={fmt}
          initial={n0}
          strings={strings}
          time={time}
          unit={unit}
        />
      );
    }
    if (mode === "final") {
      return td === null || time === null ? (
        <Notice>{strings.enter}</Notice>
      ) : (
        <FinalMode
          doubling={td}
          fmt={fmt}
          initial={n0}
          strings={strings}
          time={time}
          unit={unit}
        />
      );
    }
    return td === null || goal === null ? (
      <Notice>{strings.enter}</Notice>
    ) : (
      <TimeMode
        doubling={td}
        fmt={fmt}
        initial={n0}
        strings={strings}
        target={goal}
        unit={unit}
      />
    );
  }

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name={`${ID}-mode`}
        onChange={setMode}
        options={[
          { value: "doubling", label: strings.modeDoubling },
          { value: "final", label: strings.modeFinal },
          { value: "time", label: strings.modeTime },
        ]}
        value={mode}
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <TextField
          id={`${ID}-initial`}
          label={strings.initial}
          numeric
          onChange={setInitial}
          value={initial}
        />
        {mode === "doubling" ? (
          <TextField
            id={`${ID}-final`}
            label={strings.final}
            numeric
            onChange={setFinal}
            value={final}
          />
        ) : null}
        {mode === "time" ? (
          <TextField
            id={`${ID}-target`}
            label={strings.target}
            numeric
            onChange={setTarget}
            value={target}
          />
        ) : null}
        {mode === "doubling" || mode === "final" ? (
          <TextField
            id={`${ID}-elapsed`}
            label={strings.elapsed}
            numeric
            onChange={setElapsed}
            value={elapsed}
          />
        ) : null}
        {mode === "doubling" ? null : (
          <TextField
            id={`${ID}-doubling`}
            label={strings.doubling}
            numeric
            onChange={setDoubling}
            value={doubling}
          />
        )}
        <SelectField
          id={`${ID}-unit`}
          label={strings.timeUnit}
          onChange={setUnit}
          options={[
            { value: "min", label: strings.unitMin },
            { value: "h", label: strings.unitHour },
            { value: "d", label: strings.unitDay },
          ]}
          value={unit}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <div className={`${RESULT_CARD} space-y-4`}>{view()}</div>
        <p className="text-muted-foreground text-xs">{strings.phaseNote}</p>
      </section>
    </>
  );
}
